"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { Github, Linkedin, Twitter } from "@/components/icons/brand-icons";
import { NewsletterSignup } from "./NewsletterSignup";
import { AnimatedButton } from "@/components/ui/animated-button";
import { trackEvent } from "@/lib/analytics";
import { BRAND_NAME, SOCIAL_LINKS as BRAND_SOCIAL_LINKS } from "@/lib/config/brand";

const FOOTER_LINKS = {
  Platforms: [
    { label: "Salesforce", href: "/salesforce-health-check" },
    { label: "ServiceNow", href: "/servicenow-health-check" },
    { label: "HubSpot", href: "/hubspot-health-check" },
    { label: "Splunk (Coming Soon)", href: "/splunk-health-check" },
    { label: "All Platforms", href: "/supported-platforms" },
  ],
  Features: [
    { label: "AI Consultant", href: "/features/ai-consultant" },
    { label: "Health Check", href: "/features/health-check" },
    { label: "Security Scanner", href: "/features/security-scanner" },
    { label: "Deployment Automation", href: "/features/deployment-automation" },
    { label: "All Features", href: "/features" },
  ],
  Solutions: [
    { label: "For Salesforce Admins", href: "/use-cases/salesforce-admin" },
    { label: "For IT Executives", href: "/use-cases/it-executive" },
    { label: "For SMB Owners", href: "/use-cases/smb-owner" },
    { label: "Integrations", href: "/integrations" },
    { label: "All Comparisons", href: "/vs" },
  ],
  Resources: [
    { label: "Documentation", href: "/docs" },
    { label: "Fix Guides", href: "/fix" },
    { label: "ROI Calculator", href: "/calculator" },
    { label: "Insights Blog", href: "/insights" },
    { label: "FAQ", href: "/docs/faq" },
  ],
  Company: [
    { label: "About", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/contact" },
    { label: "Pricing", href: "/pricing" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "Beta Terms", href: "/beta-terms" },
    { label: "DPA", href: "/dpa" },
    { label: "Security", href: "/security" },
    { label: "Sitemap", href: "/sitemap" },
  ],
};

// Curated set of high-value deep pages surfaced in the footer of every page.
// Goal: give Google's crawler a stable, sitewide path to long-tail URLs that
// otherwise rely only on cluster-internal links — the largest single internal
// linking lever for an underindexed long tail.
const POPULAR_DEEP_LINKS: { group: string; links: { label: string; href: string }[] }[] = [
  {
    group: "Popular fix guides",
    links: [
      { label: "Salesforce API limits", href: "/fix/salesforce-api-limits" },
      { label: "OAuth token refresh", href: "/fix/oauth-token-refresh" },
      { label: "Duplicate contacts", href: "/fix/duplicate-contacts" },
      { label: "Deployment failures", href: "/fix/deployment-failures" },
      { label: "SOQL query timeout", href: "/fix/soql-query-timeout" },
      { label: "HubSpot API rate limits", href: "/fix/hubspot-api-rate-limit-exceeded" },
    ],
  },
  {
    group: "Top integrations",
    links: [
      { label: "Salesforce + HubSpot", href: "/integrations/salesforce-hubspot" },
      { label: "Salesforce + ServiceNow", href: "/integrations/salesforce-servicenow" },
      { label: "Salesforce + Jira", href: "/integrations/salesforce-jira" },
      { label: "Salesforce + Slack", href: "/integrations/salesforce-slack" },
      { label: "Salesforce + Marketo", href: "/integrations/salesforce-marketo" },
      { label: "ServiceNow + Jira", href: "/integrations/servicenow-jira" },
    ],
  },
  {
    group: `Compare ${BRAND_NAME}`,
    links: [
      { label: "vs Gearset", href: "/vs/gearset" },
      { label: "vs Workato", href: "/vs/workato" },
      { label: "vs MuleSoft", href: "/vs/mulesoft" },
      { label: "vs Copado", href: "/vs/copado" },
      { label: "vs Managed Services", href: "/vs/managed-services" },
      { label: "vs Hiring a Consultant", href: "/vs/hiring-consultant" },
    ],
  },
  {
    group: "By industry",
    links: [
      { label: "Manufacturing org health", href: "/resources/salesforce-org-health-manufacturing" },
      { label: "Manufacturing overview", href: "/industries/manufacturing" },
      { label: "Healthcare", href: "/industries/healthcare" },
      { label: "Financial services", href: "/industries/financial-services" },
      { label: "Retail", href: "/industries/retail" },
    ],
  },
];

const SOCIAL_LINKS = [
  { icon: Twitter, href: BRAND_SOCIAL_LINKS.x, label: "Twitter" },
  { icon: Linkedin, href: BRAND_SOCIAL_LINKS.linkedin, label: "LinkedIn" },
  { icon: Github, href: BRAND_SOCIAL_LINKS.github, label: "GitHub" },
];

export function MarketingFooter() {
  const year = new Date().getFullYear();

  const handleCta = (event: string) => () => {
    trackEvent({ event, sendInstantly: true });
  };

  return (
    <footer className="relative overflow-hidden bg-background" id="footer">
      {/* Large CTA Section */}
      <section className="relative border-b border-border py-12 sm:py-20 md:py-28">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Ready to transform your{" "}
              <span className="gradient-text-aurora">
                SaaS operations?
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Start with a free health check, then let {BRAND_NAME} automate platform management with your team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <AnimatedButton
                size="lg"
                animation="shimmer"
                className="rounded-full bg-primary text-primary-foreground px-8 shadow-[0_0_30px_rgba(37,99,235,0.4)] hover:shadow-[0_0_40px_rgba(37,99,235,0.5)]"
                asChild
                onClick={handleCta("click_cta_footer_get_started")}
              >
                <Link href="/free-health-check">
                  Get Started
                </Link>
              </AnimatedButton>
              <AnimatedButton
                size="lg"
                variant="outline"
                animation="glow"
                className="rounded-full border-border text-foreground px-8 hover:bg-muted hover:border-foreground/30"
                asChild
                onClick={handleCta("click_cta_footer_demo")}
              >
                <Link href="/contact?topic=sales">Schedule Demo</Link>
              </AnimatedButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Footer Content */}
      <div className="relative mx-auto max-w-content px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        <div className="flex flex-col gap-8 sm:gap-12">
          {/* Top Section: Logo + Links Grid */}
          <div className="flex flex-col gap-8 sm:gap-12 lg:flex-row lg:justify-between">
            {/* Logo and Description */}
            <div className="max-w-sm space-y-5">
              <Link
                href="/"
                className="flex items-center gap-2"
              >
                <Image
                  src="/logos/app-icon-dark.png"
                  alt={BRAND_NAME}
                  width={36}
                  height={36}
                  className="h-9 w-auto"
                />
                <span className="font-semibold text-foreground text-lg">{BRAND_NAME}</span>
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI-powered platform management for modern SaaS teams. Automate
                health checks, orchestrate releases, and ship with guardrails
                across every org.
              </p>
            </div>

            {/* Links Grid - 6 Columns on Desktop */}
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6 lg:gap-8">
              {Object.entries(FOOTER_LINKS).map(([section, links]) => (
                <div key={section} className="space-y-4 text-sm">
                  <p className="font-semibold text-foreground">{section}</p>
                  <ul className="space-y-2.5 text-muted-foreground">
                    {links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.href}
                          className="transition-colors duration-200 hover:text-foreground text-[13px]"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Popular deep pages — sitewide internal linking for long-tail SEO */}
          <div className="border-t border-border pt-8">
            <p className="mb-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Popular pages
            </p>
            <div className="grid gap-8 sm:grid-cols-3">
              {POPULAR_DEEP_LINKS.map((group) => (
                <div key={group.group} className="space-y-3">
                  <p className="text-sm font-medium text-foreground">{group.group}</p>
                  <ul className="space-y-2 text-[13px] text-muted-foreground">
                    {group.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href}
                          className="transition-colors duration-200 hover:text-foreground"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter Signup Section — anchor target for the /note vanity redirect */}
          <div
            id="newsletter"
            className="flex flex-col gap-4 rounded-2xl border border-border bg-muted/40 backdrop-blur-sm p-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Stay in the loop</p>
              <p className="text-sm text-muted-foreground">
                Get insights on AI-powered SaaS management delivered to your
                inbox.
              </p>
            </div>
            <NewsletterSignup
              variant="inline"
              className="w-full sm:w-auto sm:min-w-[340px]"
              placeholder="you@company.com"
              buttonText="Subscribe"
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar - Full Width with Gradient Border */}
      <div className="relative border-t border-border">
        {/* Subtle gradient line at top */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

        <div className="mx-auto max-w-content px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              &copy; {year} {BRAND_NAME}. All rights reserved.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="p-2 text-muted-foreground rounded-full transition-all duration-200 hover:text-foreground hover:bg-muted"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
